// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * DSA 1.8 vector labels (`style.label`, notes §7): a small box next to each
 * drawn feature that matches `label.when`, with the `label.template` text
 * filled from the feature's values — for tropical cyclones the storm's name
 * and wind beside its head, "SIMON 250 km/h".
 *
 * The box is filled with the feature's own colour where the label sits (a
 * track's head: the Saffir-Simpson step of the wind now), so the box says
 * the category as the track does. The 1.8 schema gives labels text only;
 * the box is how AYNI draws every label.
 *
 * `labelsOf` is pure and shared by the browser globe (vectorMapLayer.ts,
 * which also draws Studio renders) and the headset / phone AR globe
 * (vrVectorLayers.ts). `drawLabelBox` paints one box on a canvas for both.
 */
import type { CompiledVectorLayer, LonLat, VectorFeature } from './vectorModel'
import { cutTrack, unwrap } from './vectorGeo'
import { evalColor, evalNumber, fillTemplate, localize, matches, type Attrs, type Rgba } from './vectorStyle'
import type { VectorDrawItem } from './vectorTimeline'
import type { VectorSize } from './vectorTypes'

const FALLBACK: Rgba = [0.2, 0.24, 0.3, 1]
/** Longest label text, characters. */
const MAX_CHARS = 48

export interface VectorLabel {
  /** The feature's row index. */
  index: number
  lon: number
  lat: number
  text: string
  /** Box fill: the feature's colour where the label sits. */
  color: Rgba
  /** 0..1: layer opacity × the feature's animation opacity. */
  opacity: number
  /** What the box must clear at its anchor: a track's head or a point's disc (diameter). */
  clear: { value: number; size: VectorSize | undefined } | null
}

/**
 * A feature's values at data time T (Unix s): a track's per-vertex cells
 * from the vertex its drawn head has passed (§7.6: a segment takes the
 * values of the vertex it starts from); other features' values as they are.
 */
export function attrsAt(f: VectorFeature, T: number): Attrs {
  const times = f.vertexTimes
  const names = Object.keys(f.perVertex)
  if (!times?.length || !names.length) return f.attrs
  let i = 0
  while (i < times.length - 1 && times[i + 1] <= T) i++
  const out: Attrs = { ...f.attrs }
  for (const name of names) out[name] = f.perVertex[name][i]
  return out
}

/** Where a feature's label sits, or null when nothing of it is drawn. */
function anchorOf(layer: CompiledVectorLayer, it: VectorDrawItem): LonLat | null {
  const f = it.feature
  switch (layer.spec.geometry) {
    case 'point':
      return f.point ?? null
    case 'track': {
      if (!f.parts?.[0] || !f.vertexTimes) return null
      const track = layer.spec.style.track
      const window = track?.trail?.mode === 'window' ? track.trail.dataSeconds : undefined
      return cutTrack(unwrap(f.parts[0]), f.vertexTimes, it.headT, track?.interpolate !== false, window).head
    }
    case 'line': {
      const part = f.parts?.[0]
      return part?.length ? part[Math.floor((part.length - 1) / 2)] : null
    }
    case 'polygon': {
      const ring = f.polygons?.[0]?.[0]
      if (!ring?.length) return null
      const u = unwrap(ring)
      return [u.reduce((s, p) => s + p[0], 0) / u.length, u.reduce((s, p) => s + p[1], 0) / u.length]
    }
  }
}

const wrapLon = (lon: number) => ((((lon + 180) % 360) + 360) % 360) - 180

/** The labels of one layer's drawn items, in the viewer's language. */
export function labelsOf(layer: CompiledVectorLayer, items: readonly VectorDrawItem[], lang: string): VectorLabel[] {
  const spec = layer.spec.style.label
  if (!spec) return []
  const template = localize(spec.template, lang)
  if (!template) return []
  const style = layer.spec.style
  const fields = layer.spec.fields
  const track = layer.spec.geometry === 'track'
  const out: VectorLabel[] = []
  for (const it of items) {
    const opacity = layer.layerOpacity * it.opacity
    if (opacity <= 0.02 || it.size <= 0) continue
    // A track is labelled at its head, only while the head is drawn (§7.6 `label`).
    if (track && !it.head) continue
    const attrs = track ? attrsAt(it.feature, it.headT) : it.feature.attrs
    if (spec.when && !matches(spec.when, attrs)) continue
    const at = anchorOf(layer, it)
    if (!at) continue
    let text = fillTemplate(template, attrs, fields, lang, layer.epochMs, 'text').replace(/\s+/g, ' ').trim()
    if (!text) continue
    if (text.length > MAX_CHARS) text = `${text.slice(0, MAX_CHARS - 1)}…`
    const head = track ? style.track?.head : undefined
    const color = (head?.color ? evalColor(head.color, attrs, fields, FALLBACK) : null)
      ?? evalColor(style.color, attrs, fields, FALLBACK) ?? FALLBACK
    const clearSize = track ? head?.size ?? style.size : layer.spec.geometry === 'point' ? style.size : undefined
    const clearValue = clearSize ? evalNumber(clearSize.value, attrs, 0) : null
    out.push({
      index: it.feature.index, lon: wrapLon(at[0]), lat: at[1], text, color: [color[0], color[1], color[2], 1], opacity,
      clear: clearValue ? { value: clearValue * it.size, size: clearSize } : null,
    })
  }
  return out
}

/** Dark text on a light box, white on a dark one (WCAG relative luminance). */
export function labelTextColor(c: Rgba): string {
  const lin = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
  const l = 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2])
  return l > 0.18 ? '#0d1118' : '#ffffff'
}

/** The box's size and font, CSS px. */
export const LABEL_BOX = { font: 12, padX: 6, padY: 3, radius: 4, border: 1 } as const
const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'

/** A key that changes when the box's picture does. */
export function labelImageKey(l: Pick<VectorLabel, 'text' | 'color'>): string {
  return `${l.text}|${l.color.slice(0, 3).map((v) => Math.round(v * 255)).join(',')}`
}

/**
 * Paints a label box at `scale` canvas px per CSS px. Returns the canvas and
 * its size in CSS px, or null when there is no 2D canvas (tests, workers).
 */
export function drawLabelBox(
  l: Pick<VectorLabel, 'text' | 'color'>, scale: number,
  makeCanvas: (w: number, h: number) => HTMLCanvasElement | OffscreenCanvas = defaultCanvas,
): { canvas: HTMLCanvasElement | OffscreenCanvas; width: number; height: number } | null {
  const probe = makeCanvas(1, 1).getContext('2d') as CanvasRenderingContext2D | null
  if (typeof probe?.measureText !== 'function') return null
  const font = `600 ${LABEL_BOX.font}px ${FONT}`
  probe.font = font
  const textW = Math.ceil(probe.measureText(l.text).width)
  const width = textW + 2 * LABEL_BOX.padX + 2 * LABEL_BOX.border
  const height = LABEL_BOX.font + 2 * LABEL_BOX.padY + 2 * LABEL_BOX.border + 2
  const canvas = makeCanvas(Math.ceil(width * scale), Math.ceil(height * scale))
  const g = canvas.getContext('2d') as CanvasRenderingContext2D | null
  if (typeof g?.fillText !== 'function') return null
  g.scale(scale, scale)
  const [r, gr, b] = l.color.map((v) => Math.round(v * 255))
  g.beginPath()
  const box = [LABEL_BOX.border / 2, LABEL_BOX.border / 2, width - LABEL_BOX.border, height - LABEL_BOX.border] as const
  if (typeof g.roundRect === 'function') g.roundRect(...box, LABEL_BOX.radius)
  else g.rect(...box)
  g.fillStyle = `rgb(${r}, ${gr}, ${b})`
  g.fill()
  // A dark edge keeps the pale boxes (Category 1, the yellow) apart from clouds and ice.
  g.lineWidth = LABEL_BOX.border
  g.strokeStyle = 'rgba(0, 0, 0, 0.55)'
  g.stroke()
  g.font = font
  g.textBaseline = 'middle'
  g.textAlign = 'left'
  g.fillStyle = labelTextColor(l.color)
  g.fillText(l.text, LABEL_BOX.border + LABEL_BOX.padX, height / 2 + 0.5)
  return { canvas, width, height }
}

function defaultCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof document !== 'undefined') return Object.assign(document.createElement('canvas'), { width: w, height: h })
  return new OffscreenCanvas(w, h)
}
